import React, { type ReactNode } from 'react'
import { Card, Divider, List, ListItem, Stack } from '@mui/material'
import { Icon } from '@mxenabled/mx-icons'

import { Text } from '@mxenabled/mxui'

import { formatCurrency } from '../../../common'
import { buildDynamicCopy, type MakeTransferCopy } from '../../../core'

import { BalanceStepper } from './BalanceStepper'
import SaveEnoughToLiveOnBottomSection from './SaveEnoughToLiveOnBottomSection'
import {
  toNumber,
  variants,
  type MakeTransferVariant,
  type MakeTransferVariantProps,
} from './layout'

// ============================================================================
// Section building blocks (block-local)
// ============================================================================

interface MonthRow {
  label: string
  value: number
}

const SAVE_AN_EXTRA_MONTHS: readonly MonthRow[] = [
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 },
  { label: '9 months', value: 9 },
]

const SAVE_ENOUGH_MONTHS: readonly MonthRow[] = [
  { label: '1 month', value: 1 },
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 },
  { label: '9 months', value: 9 },
]

/** Bordered title + description, with an optional `leading` slot. */
const Header = ({ copy, leading }: { copy: MakeTransferCopy; leading?: ReactNode }) => (
  <Stack
    sx={{ bgcolor: 'background.paper', p: 24, borderBottom: '1px solid', borderColor: 'divider' }}
  >
    {leading}
    <Stack sx={{ gap: 8 }}>
      <Text variant="h3">{copy.header_content}</Text>
      <Text sx={{ lineHeight: '24px' }} truncate={false} variant="body1">
        {copy.description}
      </Text>
    </Stack>
  </Stack>
)

/** Save-extra only: available − expenses = total remaining. */
const RemainingCard = ({
  availableBalances,
  expenses,
  totalRemaining,
  copy,
}: {
  availableBalances: number
  expenses: number
  totalRemaining?: number
  copy: MakeTransferCopy
}) => (
  <Stack sx={{ pt: 24, px: 24 }}>
    <Card sx={{ px: 16, pt: 16, pb: 12 }}>
      <Stack
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '4px',
          gap: 12,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 8,
          mb: 12,
        }}
      >
        <Stack sx={{ width: 155, textAlign: 'center' }}>
          <Text sx={{ fontWeight: 600 }} variant="body1">
            {formatCurrency(availableBalances, '0,0.00')}
          </Text>
          <Text sx={{ color: 'text.secondary' }} variant="caption">
            {copy.available_text}
          </Text>
        </Stack>
        <Icon name="remove" size={20} />
        <Stack sx={{ width: 155, textAlign: 'center' }}>
          <Text sx={{ fontWeight: 600 }} variant="body1">
            {formatCurrency(expenses, '0,0.00')}
          </Text>
          <Text sx={{ color: 'text.secondary' }} variant="caption">
            {copy.expenses_text}
          </Text>
        </Stack>
      </Stack>
      <Stack
        sx={{
          textAlign: 'center',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Text sx={{ color: 'text.secondary' }} variant="body2">
          {copy.total_remaining_text}:
        </Text>
        <Text sx={{ fontWeight: 600 }} variant="body2">
          {formatCurrency(totalRemaining, '0,0.00')}
        </Text>
      </Stack>
    </Card>
  </Stack>
)

/** Save-enough only: total available balances as of a date. */
const TotalsFooter = ({
  availableBalances,
  date,
  copy,
}: {
  availableBalances: number
  date?: string
  copy: MakeTransferCopy
}) => (
  <Stack sx={{ pt: 6, pb: 14, px: 24, textAlign: 'right', gap: 4 }}>
    <Text truncate={false} variant="h3">
      {formatCurrency(availableBalances, '0,0.00')}
    </Text>
    {date && (
      <Text sx={{ color: 'text.secondary', fontWeight: 400 }} truncate={false} variant="subtitle2">
        {buildDynamicCopy(copy.your_available_balances_text, date) as string}
      </Text>
    )}
  </Stack>
)

/** Stepper + projected-savings table. Owns the amount state so both stay in sync. */
const SavingsProjection = ({
  bodyContent,
  bodyDescription,
  monthsTable,
  deltaAmount,
  initialAmount,
  seedAmount,
  copy,
}: {
  bodyContent: string
  bodyDescription: string
  monthsTable: readonly MonthRow[]
  deltaAmount: number
  /** Fixed starting amount; when null, seeds from `seedAmount` once it resolves. */
  initialAmount: number | null
  seedAmount?: number
  copy: MakeTransferCopy
}) => {
  const [amount, setAmount] = React.useState(initialAmount ?? seedAmount ?? 0)

  React.useEffect(() => {
    if (initialAmount == null && seedAmount != null) {
      setAmount(seedAmount)
    }
  }, [initialAmount, seedAmount])

  const lastRowIndex = monthsTable.length - 1

  return (
    <>
      <Stack sx={{ p: 24, gap: 8 }}>
        <Text sx={{ fontWeight: 600 }} truncate={false} variant="body1">
          {bodyContent}
        </Text>
        <BalanceStepper
          amount={formatCurrency(amount, '0,0.00')}
          copy={copy}
          onDecrement={() => setAmount((prev) => Math.max(0, prev - deltaAmount))}
          onIncrement={() => setAmount((prev) => prev + deltaAmount)}
        />
      </Stack>
      <Stack sx={{ gap: 8 }}>
        <Text sx={{ fontWeight: 600, mx: 24 }} truncate={false} variant="body1">
          {bodyDescription}
        </Text>
        <List sx={{ bgcolor: 'background.paper', width: '100%' }}>
          {monthsTable.map(({ label, value }, index) => (
            <React.Fragment key={value}>
              <ListItem
                disableGutters={true}
                disablePadding={true}
                sx={{ minHeight: 52, bgcolor: 'background.paper' }}
              >
                <Stack sx={{ width: '100%' }}>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      px: 24,
                      py: 8,
                    }}
                  >
                    <Text truncate={false} variant="body1">
                      {label}
                    </Text>
                    <Text sx={{ fontWeight: 600 }} truncate={false} variant="body1">
                      {formatCurrency(value * amount, '0,0')}
                    </Text>
                  </Stack>
                </Stack>
              </ListItem>
              <Divider component="li" sx={{ ml: index === lastRowIndex ? 0 : 24 }} />
            </React.Fragment>
          ))}
        </List>
      </Stack>
    </>
  )
}

// ============================================================================
// Block — one component handles both variants (they share the header + the
// stepper/table skeleton; the top/middle/bottom sections differ). Branching is
// on `variant` since the two are structurally close. If a third structurally
// distinct variant appears, split into per-variant components + a registry.
// ============================================================================

export const MakeTransferActionBlock = ({
  variant,
  slotData,
  copy,
}: MakeTransferVariantProps & { variant: MakeTransferVariant }) => {
  const isSaveEnough = variant === variants.SAVE_ENOUGH_TO_LIVE_ON

  const availableBalances = slotData?.available_balance
  const averageMonthlySpend = toNumber(slotData?.avg_monthly_spend)
  const expenses = toNumber(slotData?.upcoming_expenses)
  const totalRemaining = toNumber(slotData?.total_remaining)
  const svgDateContent = slotData?.time_period
  const date = slotData?.date

  return (
    <>
      <Header
        copy={copy}
        leading={
          isSaveEnough &&
          availableBalances != null &&
          averageMonthlySpend != null &&
          svgDateContent ? (
            <SaveEnoughToLiveOnBottomSection
              content={svgDateContent}
              leftLabel={copy.available_balances_text}
              leftValue={formatCurrency(availableBalances, '0,0.00')}
              rightLabel={copy.average_monthly_spend_text}
              rightValue={formatCurrency(averageMonthlySpend, '0,0.00')}
              sx={{ width: '100%', mb: 24 }}
            />
          ) : null
        }
      />

      {!isSaveEnough && availableBalances != null && expenses != null && (
        <RemainingCard
          availableBalances={availableBalances}
          copy={copy}
          expenses={expenses}
          totalRemaining={totalRemaining}
        />
      )}

      <SavingsProjection
        bodyContent={isSaveEnough ? copy.body_spending_content : copy.body_saving_content}
        bodyDescription={
          isSaveEnough ? copy.body_spending_description : copy.body_saving_description
        }
        copy={copy}
        deltaAmount={isSaveEnough ? 50 : 5}
        initialAmount={isSaveEnough ? null : 100}
        monthsTable={isSaveEnough ? SAVE_ENOUGH_MONTHS : SAVE_AN_EXTRA_MONTHS}
        seedAmount={isSaveEnough ? averageMonthlySpend : undefined}
      />

      {isSaveEnough && availableBalances != null && (
        <TotalsFooter availableBalances={availableBalances} copy={copy} date={date} />
      )}
    </>
  )
}
